import { Box, Card, CardContent, Typography, IconButton, useTheme, alpha, Chip, Tooltip } from "@mui/material";
import { PublicDocumentEntity } from "api/generated";
import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DescriptionIcon from '@mui/icons-material/Description';
import GetAppIcon from '@mui/icons-material/GetApp';

interface Base {
    id: number,
    name: string
}

interface Props {
    sections: Base[],
    sectionRecords: Record<number, PublicDocumentEntity[]>,
    isManager: boolean,
    handleOpenconfirmDeleteCallback: (id: number) => void,
    updateCallBack: (data: PublicDocumentEntity) => void,
    icon?: ReactNode,
    compact?: boolean
}

const RecordAccordion: FC<Props> = ({ 
    sections, 
    sectionRecords, 
    isManager, 
    handleOpenconfirmDeleteCallback, 
    updateCallBack,
    icon,
    compact = false
}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    if (compact) {
        // Compact single document card
        return (
            <>
                {sections.map((section) => {
                    if (sectionRecords[section.id]?.length > 0) {
                        const record = sectionRecords[section.id][0];
                        return (
                            <Card
                                key={section.id}
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        boxShadow: theme.shadows[4],
                                        transform: 'translateY(-2px)',
                                        borderColor: theme.palette.primary.main,
                                    }
                                }}
                            >
                                <CardContent sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                        <Box sx={{
                                            p: 1,
                                            borderRadius: 1.5,
                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}>
                                            {icon || <DescriptionIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography 
                                                variant="body2" 
                                                fontWeight="600" 
                                                color="text.primary"
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    lineHeight: 1.4
                                                }}
                                            >
                                                {record.name}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 'auto', pt: 1 }}>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title={t('common.download') || 'Download'}>
                                                <IconButton
                                                    size="small"
                                                    component="a"
                                                    href={record.filePath}
                                                    download
                                                    target="_blank"
                                                    sx={{
                                                        color: theme.palette.primary.main,
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.primary.main, 0.1)
                                                        }
                                                    }}
                                                >
                                                    <GetAppIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                            {isManager && (
                                                <>
                                                    <Tooltip title={t('publicDocuments.update') || 'Update'}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => updateCallBack(record)}
                                                            sx={{
                                                                color: theme.palette.info.main,
                                                                '&:hover': {
                                                                    bgcolor: alpha(theme.palette.info.main, 0.1)
                                                                }
                                                            }}
                                                        >
                                                            <EditIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t('common.delete') || 'Delete'}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenconfirmDeleteCallback(record.id)}
                                                            sx={{
                                                                color: theme.palette.error.main,
                                                                '&:hover': {
                                                                    bgcolor: alpha(theme.palette.error.main, 0.1)
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    }
                    return null;
                })}
            </>
        );
    }

    // Full section with grouped documents
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sections.map((section) => {
                if (sectionRecords[section.id]?.length > 0) {
                    return (
                        <Box key={section.id}>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1.5, 
                                mb: 1.5,
                                px: 1
                            }}>
                                {icon && (
                                    <Box sx={{ 
                                        color: theme.palette.primary.main,
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                        {icon}
                                    </Box>
                                )}
                                <Typography variant="h6" fontWeight="600" color="text.primary">
                                    {section.name}
                                </Typography>
                                <Chip 
                                    label={sectionRecords[section.id].length} 
                                    size="small" 
                                    sx={{ 
                                        height: 20,
                                        fontSize: '0.7rem',
                                        bgcolor: alpha(theme.palette.primary.main, 0.1), 
                                        color: theme.palette.primary.main 
                                    }}
                                />
                            </Box>
                            
                            <Box sx={{ 
                                display: 'grid', 
                                gridTemplateColumns: { 
                                    xs: '1fr', 
                                    sm: 'repeat(2, 1fr)', 
                                    md: 'repeat(3, 1fr)', 
                                    lg: 'repeat(4, 1fr)' 
                                },
                                gap: 2
                            }}>
                                {sectionRecords[section.id].map((record) => (
                                    <Card
                                        key={record.id}
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            borderRadius: 2,
                                            border: `1px solid ${theme.palette.divider}`,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                boxShadow: theme.shadows[4],
                                                transform: 'translateY(-2px)',
                                                borderColor: theme.palette.primary.main,
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                <Box sx={{
                                                    p: 1.5,
                                                    borderRadius: 1.5,
                                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    <DescriptionIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography 
                                                        variant="body1" 
                                                        fontWeight="600" 
                                                        color="text.primary"
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            lineHeight: 1.4,
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        {record.name}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'space-between', 
                                                mt: 'auto',
                                                pt: 1,
                                                borderTop: `1px solid ${theme.palette.divider}`
                                            }}>
                                                <Tooltip title={t('common.download') || 'Download'}>
                                                    <IconButton
                                                        size="small"
                                                        component="a"
                                                        href={record.filePath}
                                                        download
                                                        target="_blank"
                                                        sx={{
                                                            color: theme.palette.primary.main,
                                                            '&:hover': {
                                                                bgcolor: alpha(theme.palette.primary.main, 0.1)
                                                            }
                                                        }}
                                                    >
                                                        <GetAppIcon sx={{ fontSize: 20 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                
                                                {isManager && (
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Tooltip title={t('publicDocuments.update') || 'Update'}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => updateCallBack(record)}
                                                                sx={{
                                                                    color: theme.palette.info.main,
                                                                    '&:hover': {
                                                                        bgcolor: alpha(theme.palette.info.main, 0.1)
                                                                    }
                                                                }}
                                                            >
                                                                <EditIcon sx={{ fontSize: 20 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={t('common.delete') || 'Delete'}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenconfirmDeleteCallback(record.id)}
                                                                sx={{
                                                                    color: theme.palette.error.main,
                                                                    '&:hover': {
                                                                        bgcolor: alpha(theme.palette.error.main, 0.1)
                                                                    }
                                                                }}
                                                            >
                                                                <DeleteIcon sx={{ fontSize: 20 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        </Box>
                    );
                }
                return null;
            })}
        </Box>
    )
}

export default RecordAccordion;
